
import * as THREE from "../../../libs/three.js/build/three.module.js";
import {Line2} from "../../../libs/three.js/lines/Line2.js";
import {LineGeometry} from "../../../libs/three.js/lines/LineGeometry.js";
import {LineMaterial} from "../../../libs/three.js/lines/LineMaterial.js";
import {DigitizationTools} from "../ToolRegistry.js";
import {FeatureVisual} from "../FeatureVisual.js";
import {createFeature} from "../Feature.js";
import {computeScreenSpaceScale, ScreenSpaceSizes} from "../ScreenSpaceScale.js";
import {trackPipe, TRACKING_DEFAULTS, computeTopLine, polylineVertexTangents} from "../geometry/pipeTracking.js";
import {createOctreeRoiQuery} from "../geometry/pipeRoiAdapter.js";
import {PipeTraceVisual} from "../PipeTraceVisual.js";

import {t, T} from "../i18n/index.js";
// "Find rør": the user points out a pipe (2 direction clicks along it + ≥3 clicks on its visible surface); the tool follows
// the pipe through the point cloud (geometry/pipeTracking.js) and creates exactly two ordinary registrations:
//
//   centre-line  - the pipe's computed axis (LineString, authoritative, editable)
//   top-line     - the highest point of the circular cross-section along it (LineString, DERIVED from the centre-line + R)
//
// Nothing else is produced or stored: no report, no diagnostics, no quality panel. While it runs the user sees the progress
// text and a growing preview line; a failure gives one short message and creates nothing.
//
// Context: the calculation reads only the point cloud of the ACTIVE model (the one the result is attached to). A run is
// abandoned silently - no result, no message, preview removed - on ESC, a tool switch, an active-model change, the cloud
// leaving the scene, or a newer run. Point loading failures are reported as such (not as "no pipe found").
//
// The top-line is kept consistent with the centre-line by ONE listener per DigitizationModule (ensureTopLineLink), keyed by
// the pair id stored on both registrations - not by a closure - so it also works after a reload or a shared-model sync.

export const PIPE_TRACE_TOOL_ID = "digitize_pipe_trace";
const DIRECTION_COLOR = 0x00e5ff;
const SEED_COLOR = 0xffdc00;
const MIN_SEEDS = 3;
// a preview frame every 80-250 ms of tracking: 3x the (smoothed) cost of a frame, which renders the whole point cloud, so
// the live line costs ~25 % extra time - but never so rarely that it stops looking like it grows
const PREVIEW_INTERVAL_MS = 80;
const PREVIEW_INTERVAL_MAX_MS = 250;
const PREVIEW_MAX_SHARE = 0.25;
// a frame that takes longer than this means the browser is throttling the page (hidden or covered window: ~1 frame/s);
// the run then stops painting and just computes, or every yield would cost a second
const PREVIEW_STALL_MS = 250;

export const PipeTraceTool = {
	id: PIPE_TRACE_TOOL_ID,
	name: T("Find rør"),
	category: "specialized",
	// a registration tool: the result is two registrations on the pipe (user decision, Registreringer/Målinger task)
	kind: "registration",
	toolbarGroup: "registration",
	toolbarOrder: 130,
	icon: "find_pipe.svg",
	resultGeometryType: "LineString",
	featureType: "PipeTrace",
	tooltip: T("Følg et synligt rør automatisk gennem punktskyen ud fra en brugerdefineret start."),
	instructions: {
		direction1: T("Retning 1/2: Venstreklik på røret for at angive rørets omtrentlige længderetning."),
		direction2: T("Retning 2/2: Venstreklik længere fremme langs røret."),
		seed: (n) => t("Røroverflade ({0}): Venstreklik på den synlige røroverflade. Fordel punkterne. Højreklik afslutter (mindst {1}).", n, MIN_SEEDS),
		helpId: "pipe-trace",
	},
	customCapture(dm, tool){
		runPipeTraceCapture(dm, tool);
	},
	// restore hook (persistence / import): make sure the top-line follows edits of its centre-line after a reload as well
	decorateLoaded(visual, feature, dm){
		ensureTopLineLink(dm);
	},
};

DigitizationTools.register(PipeTraceTool);

// ── capture-phase markers (disposed with all their GPU resources) ─────────
function createMarker(color){
	const mesh = new THREE.Mesh(new THREE.SphereGeometry(1, 12, 12),
		new THREE.MeshLambertMaterial({color, depthTest: false, depthWrite: false}));
	mesh.visible = false;
	return mesh;
}
function createDashedLine(color){
	const g = new LineGeometry();
	g.setPositions([0, 0, 0, 0, 0, 0]);
	const m = new LineMaterial({color, linewidth: 2, dashSize: 3, gapSize: 2, dashed: true, resolution: new THREE.Vector2(1000, 1000)});
	m.depthTest = false;
	const line = new Line2(g, m);
	line.computeLineDistances();
	line.visible = false;
	return line;
}

class CaptureHelper {
	constructor(dm){
		this.dm = dm;
		this.group = new THREE.Group();
		this.directionMarkers = [createMarker(DIRECTION_COLOR), createMarker(DIRECTION_COLOR)];
		this.directionLine = createDashedLine(DIRECTION_COLOR);
		this.seedMarkers = [];
		for(const m of this.directionMarkers) this.group.add(m);
		this.group.add(this.directionLine);
		dm.scene.add(this.group);
		this._onUpdate = () => this._updateScales();
		dm.viewer.addEventListener("update", this._onUpdate);
		this.disposed = false;
	}
	setDirectionPoint(i, pos){
		const m = this.directionMarkers[i];
		m.position.copy(pos); m.visible = true;
		if(this.directionMarkers[0].visible && this.directionMarkers[1].visible){
			const a = this.directionMarkers[0].position, b = this.directionMarkers[1].position;
			this.directionLine.position.copy(a);
			this.directionLine.geometry.setPositions([0, 0, 0, ...b.clone().sub(a).toArray()]);
			this.directionLine.computeLineDistances();
			this.directionLine.visible = true;
		}
	}
	addSeedPoint(pos){
		const m = createMarker(SEED_COLOR);
		m.position.copy(pos); m.visible = true;
		this.group.add(m); this.seedMarkers.push(m);
	}
	_updateScales(){
		const camera = this.dm.viewer.scene.getActiveCamera();
		const size = this.dm.viewer.renderer.getSize(new THREE.Vector2());
		for(const m of [...this.directionMarkers, ...this.seedMarkers]){
			if(!m.visible) continue;
			const s = computeScreenSpaceScale(camera, m.getWorldPosition(new THREE.Vector3()), camera.position, size.width, size.height, ScreenSpaceSizes.vertex);
			m.scale.setScalar(s);
		}
		if(this.directionLine.visible) this.directionLine.material.resolution.set(size.width, size.height);
	}
	dispose(){
		if(this.disposed) return;
		this.disposed = true;
		this.dm.viewer.removeEventListener("update", this._onUpdate);
		this.dm.scene.remove(this.group);
		for(const m of [...this.directionMarkers, ...this.seedMarkers]){ m.geometry.dispose(); m.material.dispose(); }
		this.directionLine.geometry.dispose();
		this.directionLine.material.dispose();
		this.seedMarkers = [];
	}
}

// ── capture primitives ─────────────────────────────────────────────────
function captureFixedPoints(dm, count, getText, onEach){
	return new Promise((resolve) => {
		const dom = dm.viewer.renderer.domElement;
		const pts = [];
		let done = false;
		const cleanup = () => {
			dom.removeEventListener("mouseup", onUp, false);
			dm.viewer.removeEventListener("cancel_insertions", onCancel);
		};
		const onCancel = () => { if(done) return; done = true; cleanup(); resolve(null); };
		const onUp = (e) => {
			if(done || dm.isPaused() || dm._consumeCopyClickGuard()) return;
			if(e.button !== THREE.MOUSE.LEFT) return;
			const loc = dm._pickPlacementPosition(e);
			if(!loc) return;
			pts.push(loc.clone());
			dm._markOperationStarted();
			onEach(loc.clone(), pts.length);
			if(pts.length >= count){ done = true; cleanup(); resolve(pts); }
			else dm._showInstruction(t("Find rør"), getText(pts.length + 1));
		};
		dom.addEventListener("mouseup", onUp, false);
		dm.viewer.addEventListener("cancel_insertions", onCancel);
	});
}

// left-click adds; right-click finishes once >= minCount; ESC/cancel -> null
function captureVariablePoints(dm, minCount, getText, onEach){
	return new Promise((resolve) => {
		const dom = dm.viewer.renderer.domElement;
		const pts = [];
		let done = false;
		const cleanup = () => {
			dom.removeEventListener("mouseup", onUp, false);
			dm.viewer.removeEventListener("cancel_insertions", onCancel);
		};
		const onCancel = () => { if(done) return; done = true; cleanup(); resolve(null); };
		const onUp = (e) => {
			if(done || dm.isPaused() || dm._consumeCopyClickGuard()) return;
			if(e.button === THREE.MOUSE.RIGHT){
				if(pts.length >= minCount){ done = true; cleanup(); resolve(pts); }
				return;
			}
			if(e.button !== THREE.MOUSE.LEFT) return;
			const loc = dm._pickPlacementPosition(e);
			if(!loc) return;
			pts.push(loc.clone());
			dm._markOperationStarted();
			onEach(loc.clone(), pts.length);
			dm._showInstruction(t("Find rør"), getText(pts.length + 1));
		};
		dom.addEventListener("mouseup", onUp, false);
		dm.viewer.addEventListener("cancel_insertions", onCancel);
	});
}

// one rendered frame - or 50 ms when the tab is hidden and requestAnimationFrame does not fire (the run must not stall)
function nextFrame(){
	return new Promise((resolve) => {
		let done = false;
		const finish = () => { if(!done){ done = true; resolve(); } };
		requestAnimationFrame(finish);
		setTimeout(finish, 50);
	});
}

// ── context: which point cloud, and is this run still current? ─────────
// The point cloud of the active model (PointView names a scene point cloud by its pointcloud id); without a host model
// concept (standalone Potree) the single visible cloud. Returns {pointcloud} or {error}.
function resolveSourceCloud(dm){
	const clouds = (dm.viewer.scene.pointclouds || []).filter((pc) => pc && pc.visible);
	const active = dm.activePointcloudId;
	if(active != null){
		const pc = clouds.find((c) => c.name === active);
		return pc ? {pointcloud: pc} : {error: t("Den aktive model er ikke indlæst eller er skjult.")};
	}
	if(clouds.length === 1) return {pointcloud: clouds[0]};
	return {error: t("Vælg den aktive model, før du bruger Find rør.")};
}

let currentRun = null;

function beginRun(dm, tool, pointcloud){
	if(currentRun) currentRun.cancel();
	const run = {
		cancelled: false,
		pointcloud,
		pointcloudId: dm.activePointcloudId,
		cancel(){ this.cancelled = true; },
		// stale = this run may no longer produce anything (no result, no message)
		isStale(){
			return this.cancelled || currentRun !== this || dm.activeTool !== tool
				|| dm.activePointcloudId !== this.pointcloudId
				|| (dm.viewer.scene.pointclouds || []).indexOf(this.pointcloud) === -1;
		},
	};
	currentRun = run;
	return run;
}

// ── the workflow ───────────────────────────────────────────────────────
async function runPipeTraceCapture(dm, tool){
	const viewer = dm.viewer;
	const source = resolveSourceCloud(dm);
	if(source.error){
		if(dm.activeTool === tool) dm.stopInsertion();
		viewer.postMessage(t("Find rør: {0}", source.error), {duration: 6000});
		return;
	}
	const run = beginRun(dm, tool, source.pointcloud);
	const helper = new CaptureHelper(dm);
	const previews = {};
	let roi = null;
	const onCancelInsertions = () => run.cancel();
	const onToolChanged = (e) => { if(e.tool !== tool) run.cancel(); };
	viewer.addEventListener("cancel_insertions", onCancelInsertions);
	dm.addEventListener("active_tool_changed", onToolChanged);

	// everything this run allocated goes away on every exit path
	const releaseAll = () => {
		viewer.removeEventListener("cancel_insertions", onCancelInsertions);
		dm.removeEventListener("active_tool_changed", onToolChanged);
		helper.dispose();
		for(const k of Object.keys(previews)){ previews[k].dispose(); delete previews[k]; }
		if(roi){ roi.dispose(); roi = null; }
		if(currentRun === run) currentRun = null;
	};
	const finishTool = () => {
		if(dm.activeTool === tool) dm.stopInsertion();
		else dm._hideToolHint();
	};

	try {
		// --- direction phase ---
		dm._showInstruction(t("Find rør"), tool.instructions.direction1);
		const directionPoints = await captureFixedPoints(dm, 2,
			(n) => n === 1 ? tool.instructions.direction1 : tool.instructions.direction2,
			(p, i) => helper.setDirectionPoint(i - 1, p));
		if(!directionPoints || run.isStale()){ releaseAll(); if(!run.cancelled) finishTool(); return; }

		// --- surface phase ---
		dm._showInstruction(t("Find rør"), tool.instructions.seed(1));
		const surfacePoints = await captureVariablePoints(dm, MIN_SEEDS,
			(n) => tool.instructions.seed(n),
			(p) => helper.addSeedPoint(p));
		if(!surfacePoints || run.isStale()){ releaseAll(); if(!run.cancelled) finishTool(); return; }
		helper.dispose(); // markers no longer needed

		const stage = (text) => { if(!run.isStale()) dm._showInstruction(t("Find rør"), t("{0}  (ESC afbryder)", text)); };
		// Live preview: the tracker reports every section (cheap - only the latest report is kept) and offers to yield after
		// each; every PREVIEW_INTERVAL_MS (longer when frames are expensive) the latest state goes into the preview + the
		// status text and the main thread is handed to the browser for one frame, so the line visibly grows forward and
		// backward (and ESC is seen promptly).
		let latest = null, lastYieldAt = 0, frameCostMs = 0, previewOff = false;
		const onProgress = (p) => { latest = p; };
		const showLatest = () => {
			if(!latest || run.isStale()) return;
			const p = latest;
			latest = null;
			if(!previews.live){ previews.live = new PipeTraceVisual(); dm.scene.add(previews.live); previews.live.attach(viewer); }
			previews.live.updateTrack(p.direction, p.centers, p.tip);
			const dir = p.direction === "forward" ? t("Sporer fremad") : t("Sporer bagud");
			stage(t("{0}… {1} m, {2} sektioner", dir, p.followedLengthM.toFixed(1), p.acceptedCount));
		};
		const onYield = async (force) => {
			if(previewOff || (typeof document !== "undefined" && document.hidden)) return;
			const now = performance.now();
			const interval = Math.min(PREVIEW_INTERVAL_MAX_MS, Math.max(PREVIEW_INTERVAL_MS, frameCostMs * (1 - PREVIEW_MAX_SHARE) / PREVIEW_MAX_SHARE));
			if(!force && now - lastYieldAt < interval) return;
			showLatest();
			await nextFrame();
			lastYieldAt = performance.now();
			const cost = lastYieldAt - now;
			if(cost > PREVIEW_STALL_MS) previewOff = true;
			frameCostMs = frameCostMs ? 0.7 * frameCostMs + 0.3 * cost : cost;
		};

		roi = createOctreeRoiQuery([run.pointcloud], {shouldCancel: () => run.isStale()});

		// colour at each surface click (supervision for the appearance support); optional, never blocks tracking
		try {
			const dir0 = directionPoints[1].clone().sub(directionPoints[0]).normalize();
			for(const sp of surfacePoints){
				if(run.isStale()) break;
				const near = await roi.queryPoints(sp.clone(), dir0, 0.05, 0.05);
				let best = null, bd = Infinity;
				for(const p of near){ if(!p.rgb) continue; const d = p.distanceToSquared(sp); if(d < bd){ bd = d; best = p; } }
				if(best && best.rgb) sp.rgb = [best.rgb[0], best.rgb[1], best.rgb[2]];
			}
		} catch(e){ /* colour is optional */ }
		if(run.isStale()){ releaseAll(); if(!run.cancelled) finishTool(); return; }

		stage(t("Initialiserer…"));
		await nextFrame();
		let result;
		try {
			result = await trackPipe({
				queryPoints: roi.queryPoints,
				initialization: {directionPoints, surfacePoints},
				options: {shouldCancel: () => run.isStale(), onProgress, onYield, onStage: stage},
			});
		} catch(err){
			result = {status: "error", error: err};
		}

		// abandoned on the way (ESC, tool / model switch, newer run): nothing is created and nothing is said
		if(run.isStale() || (result && result.status === "cancelled")){
			releaseAll();
			if(!run.cancelled && dm.activeTool === tool) finishTool();
			else dm._hideToolHint();
			return;
		}

		if(!result || result.status !== "ok"){
			releaseAll();
			finishTool();
			viewer.postMessage(t("Find rør: {0}", failureMessage(result)), {duration: 8000});
			return;
		}

		// success: one atomic commit of the pair (synchronous - nothing can change the context in between)
		try {
			commitResult(dm, tool, result);
		} catch(err){
			releaseAll();
			finishTool();
			viewer.postMessage(t("Find rør: Resultatet kunne ikke registreres ({0}).", (err && err.message) || err), {duration: 8000});
			return;
		}
		releaseAll();
		finishTool();
	} catch(err){
		releaseAll();
		finishTool();
		viewer.postMessage(t("Find rør: {0}", t("Beregningen fejlede ({0}).", (err && err.message) || err)), {duration: 8000});
	}
}

function failureMessage(result){
	const status = result && result.status;
	if(status === "error"){
		const err = result.error;
		if(err && err.pointLoadFailed) return t("Punkterne kunne ikke indlæses. Prøv igen.");
		return t("Beregningen fejlede ({0}).", (err && err.message) || String(err || t("ukendt fejl")));
	}
	if(status === "unstable_initialization" && result.initialization && result.initialization.detail === "seed_points_not_across_pipe"){
		return t("Overfladepunkterne viser ikke tydeligt rørets bue. Klik dem på tværs af røret, så de dækker hele den synlige bredde.");
	}
	if(status === "unstable_initialization") return t("Kunne ikke initialisere en stabil rørmodel. Prøv med bedre retnings-/overfladepunkter.");
	if(status === "too_steep") return t("Røret er for stejlt til en toplinje (mere end {0}° fra vandret).", TRACKING_DEFAULTS.maxAxisInclinationDeg);
	return t("Der blev ikke fundet et rør, der kan følges fra startpunkterne.");
}

// ── commit: exactly one centre-line + one top-line, or nothing ──────────
function newPairId(){
	if(typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
	return "pt-" + Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 10);
}

function commitResult(dm, tool, result){
	const R = result.radius;
	const pairId = newPairId();
	const n = dm.featureManager.features.filter((f) => f.properties && f.properties.tool === tool.id && f.properties.attributes
		&& f.properties.attributes.role === "centerline").length + 1;
	const created = [];
	const addLine = (points, name, featureType, attributes) => {
		const visual = new FeatureVisual("LineString");
		dm.scene.add(visual);
		visual.setPoints(points.map((p) => p.clone()));
		const feature = createFeature({
			geometryType: "LineString",
			coordinates: visual.toCoordinates(),
			category: tool.category,
			tool: tool.id,
			featureType,
			name,
			attributes,
		});
		visual.setInfo(feature.properties.name, feature.properties.description);
		dm._wireFeatureLifecycle(feature, visual);
		created.push(feature.id);
		return {feature, visual};
	};
	try {
		// minimal attributes: the role, the pair id that ties the two together, and the radius the top-line is derived with
		const centre = addLine(result.centerline, t("{0} {1} – Centerlinje", tool.name, n), "PipeTrace",
			{role: "centerline", pipeTraceId: pairId, radiusM: R});
		const top = addLine(result.topLine, t("{0} {1} – Toplinje", tool.name, n), "PipeTraceTopLine",
			{role: "topline", pipeTraceId: pairId, radiusM: R});
		ensureTopLineLink(dm);
		dm.dispatchEvent({type: "feature_created", feature: centre.feature, visual: centre.visual});
		dm.dispatchEvent({type: "feature_created", feature: top.feature, visual: top.visual});
	} catch(err){
		for(const id of created) if(dm.getVisual(id)) dm.removeFeature(id); // only this run's own objects
		throw err;
	}
}

// ── centre-line → top-line link (one listener per module, survives reloads) ──
const linkedModules = new WeakSet();

export function topLineFor(centerCoordinates, radius){
	const cl = centerCoordinates.map(([x, y, z]) => new THREE.Vector3(x, y, z));
	if(cl.length < 2 || !(radius > 0)) return null;
	return computeTopLine(cl, polylineVertexTangents(cl), radius).map((p) => [p.x, p.y, p.z]);
}

export function ensureTopLineLink(dm){
	if(!dm || linkedModules.has(dm)) return;
	linkedModules.add(dm);
	let applying = false;
	dm.featureManager.addEventListener("feature_changed", (e) => {
		const f = e && e.feature;
		if(applying || !f || !f.properties || f.properties.tool !== PIPE_TRACE_TOOL_ID) return;
		const a = f.properties.attributes || {};
		if(a.role !== "centerline" || !a.pipeTraceId || !f.geometry || f.geometry.type !== "LineString") return;
		const top = dm.featureManager.features.find((x) => x.properties && x.properties.tool === PIPE_TRACE_TOOL_ID
			&& x.properties.attributes && x.properties.attributes.role === "topline" && x.properties.attributes.pipeTraceId === a.pipeTraceId);
		if(!top) return;
		const coords = topLineFor(f.geometry.coordinates, a.radiusM);
		if(!coords) return;
		const cur = top.geometry && top.geometry.coordinates;
		const same = cur && cur.length === coords.length
			&& coords.every((c, i) => Math.abs(c[0] - cur[i][0]) < 1e-9 && Math.abs(c[1] - cur[i][1]) < 1e-9 && Math.abs(c[2] - cur[i][2]) < 1e-9);
		if(same) return;
		applying = true;
		try { dm.setFeatureGeometryFromData(top.id, {type: "LineString", coordinates: coords}); }
		finally { applying = false; }
	});
}
